import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';
import ProfileCard from '../Profile/ProfileCard';
import MatchActions from './MatchActions';
import MatchEmpty from './MatchEmpty';
import MatchModal from './MatchModal';
import { removeMatch, pushAction, popAction, clearActionHistory, addRecentlyActedId, resetExhausted, setLoading, setMatchList } from '../../Redux/store';
import { supabase } from '../../supabase/client';

function MatchPage() {
  const dispatch = useDispatch();
  const authUserId = useSelector((s) => s.user.authUserId);
  const matchList = useSelector((s) => s.matches.list);
  const actionHistory = useSelector((s) => s.matches.actionHistory);
  const exhausted = useSelector((s) => s.matches.exhausted);
  const loading = useSelector((s) => s.matches.loading);
  const [resetLoading, setResetLoading] = useState(false);
  const [matchedProfile, setMatchedProfile] = useState(null);

  const top = matchList[0];

  // Handle like/pass actions on a profile
  const act = async (targetId, action) => {
    if (!authUserId) return;

    const profile = matchList.find((p) => p.id === targetId);
    if (!profile) return;

    // Mark profile as recently acted on and remove from match list
    dispatch(addRecentlyActedId(targetId));
    dispatch(removeMatch(targetId));

    if (action === 'like') {
      // Call RPC to record like and check for mutual match
      const { data } = await supabase.rpc('like_user', { p_actor_id: authUserId, p_target_id: targetId });
      if (data && data[0]?.is_mutual) {
        setMatchedProfile(profile);
      }
    } else {
      // Record pass in action history for undo
      dispatch(pushAction({ profile, action }));
      await supabase.rpc('pass_user', { p_actor_id: authUserId, p_target_id: targetId });
    }
  };

  // Undo the last pass action (local only, no RPC call)
  const undo = async () => {
    const lastAction = actionHistory[actionHistory.length - 1];
    if (!lastAction || !authUserId) return;

    // Remove from action history
    dispatch(popAction());

    // Restore profile to front of match list
    dispatch(setMatchList([lastAction.profile, ...matchList]));

    // No RPC call - DB still has pass, will be overwritten if user likes
  };

  // Reset all passes and reload matches
  const resetAllPasses = async () => {
    if (!authUserId) return;

    setResetLoading(true);

    // Clear all pass records in database
    await supabase.rpc('reset_all_passes', { p_actor_id: authUserId });

    // Clear local action history and reset exhausted flag
    dispatch(clearActionHistory());
    dispatch(resetExhausted());
    setResetLoading(false);
  };

  // Trigger match loader to check for more matches
  const checkForMore = () => {
    dispatch(resetExhausted());
  };

  if (!top) {
    return (
      <>
        <MatchEmpty 
          loading={loading} 
          onReset={resetAllPasses} 
          onCheckMore={checkForMore}
          exhausted={exhausted}
        />
        <MatchModal profile={matchedProfile} onClose={() => setMatchedProfile(null)} />
      </>
    );
  }

  return (
    <div className="relative h-full">
      <div className="h-full overflow-y-auto">
        <ProfileCard profileData={top} />
      </div>
      <MatchActions
        canUndo={actionHistory.length > 0}
        onLike={() => act(top.id, 'like')}
        onPass={() => act(top.id, 'pass')}
        onUndo={undo}
      />

      <MatchModal profile={matchedProfile} onClose={() => setMatchedProfile(null)} />
    </div>
  );
}

export default MatchPage;
